import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ROUTES } from '../../../constants/Constants';
import useAppNavigation from '../../../hooks/useAppNavigation';
import type { AppSsrFetchedDataType } from '../../../server/AppSsr';
import { getEnvironmentVariable } from '../../../utils/EnvironmentHelper';

interface FooterProps {
  ssrFetchedData: AppSsrFetchedDataType;
}

const Footer: React.FC<FooterProps> = ({ ssrFetchedData }) => {
  const { t } = useTranslation();
  const appVersion = getEnvironmentVariable('VITE_APP_VERSION');
  const appRuntimeEnv = getEnvironmentVariable('REACT_APP_RUNTIME_PROVIDER');
  const { toLocalizedRoute } = useAppNavigation();

  const appConfiguration = ssrFetchedData.appConfiguration;
  const serviceName = appConfiguration?.service.name;
  const companyName = appConfiguration?.company.legacy_entity_name;
  const companyAddress = appConfiguration?.company.legacy_entity_address;
  const companyCF = appConfiguration?.company.legacy_entity_cf;
  const companyVAT = appConfiguration?.company.legacy_entity_vat_number;

  return (
    <footer className="mb-[4px] flex min-w-full flex-col items-center justify-center px-4 pb-20 pt-48 text-center text-sm md:pb-0">
      <div className="flex flex-col items-center justify-center text-center text-xs font-bold text-slate-600">
        <div className="hidden min-w-full sm:block">
          v{`${appVersion ?? '0.0.0-unknown'}_${appRuntimeEnv ?? 'lr'}`}
        </div>
        <div className="flex min-w-full flex-col items-center justify-center gap-2 divide-x divide-none text-center sm:flex-row sm:divide-solid">
          <span className="flex flex-col items-center justify-end px-2">
            <Link to={toLocalizedRoute(ROUTES.TERMS_AND_CONDITIONS_ROUTE)}>
              {t('static.footer.terms_of_service')}
            </Link>
          </span>
          <span className="flex flex-col items-center justify-center px-2">
            <Link to={toLocalizedRoute(ROUTES.PRIVACY_POLICY_ROUTE)}>
              {t('static.footer.privacy_policy')}
            </Link>
          </span>
          <span className="flex flex-col items-center justify-start px-2">
            <Link to={toLocalizedRoute(ROUTES.COOKIE_POLICY_ROUTE)}>
              {t('static.footer.cookie_policy')}
            </Link>
          </span>
        </div>
        <div className="min-w-full">
          {t('static.footer.company_info', {
            service_name: serviceName,
            start_year: 2023,
            current_year: new Date().getFullYear(),
            company_name: companyName,
            company_address: companyAddress,
            company_cf: companyCF,
            company_vat: companyVAT,
          })}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
